import { useEffect, useState } from 'react';
import { DollarSign, TrendingUp, Users, RefreshCw, BarChart3 } from 'lucide-react';
import api from '../services/api';

interface RevenueDashboardData {
  total_mrr: number;
  total_revenue: number;
  active_customers: number;
  by_product: { product: string; total: number }[];
  by_organization: { organization: string; total: number }[];
  monthly_trend: { month: string; total: number }[];
}

function formatCurrency(n: number) {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function RevenueDashboard() {
  const [data, setData] = useState<RevenueDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/revenue/dashboard');
      setData(res.data);
      setError('');
    } catch {
      setError('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post('/revenue/sync');
      await fetchData();
    } catch {
      setError('Stripe sync failed');
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display" style={{ color: 'var(--text-primary)' }}>
            Revenue Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            Stripe revenue attribution and analytics
          </p>
        </div>
        <button
          onClick={triggerSync}
          disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          style={{
            background: syncing ? 'var(--bg-tertiary)' : 'var(--netrun-green)',
            color: syncing ? 'var(--text-muted)' : '#000',
          }}
        >
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
          {syncing ? 'Syncing...' : 'Sync Stripe'}
        </button>
      </div>

      {error && (
        <div className="p-3 rounded-lg text-sm" style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-20" style={{ color: 'var(--text-muted)' }}>Loading...</div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp size={20} style={{ color: 'var(--netrun-green)' }} />
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Monthly Recurring Revenue</span>
              </div>
              <div className="text-3xl font-display" style={{ color: 'var(--netrun-green)' }}>
                {formatCurrency(data.total_mrr)}
              </div>
            </div>
            <div className="p-5 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
              <div className="flex items-center gap-3 mb-2">
                <DollarSign size={20} style={{ color: 'var(--netrun-green)' }} />
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Total Revenue</span>
              </div>
              <div className="text-3xl font-display" style={{ color: 'var(--text-primary)' }}>
                {formatCurrency(data.total_revenue)}
              </div>
            </div>
            <div className="p-5 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
              <div className="flex items-center gap-3 mb-2">
                <Users size={20} style={{ color: 'var(--netrun-green)' }} />
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Active Subscribers</span>
              </div>
              <div className="text-3xl font-display" style={{ color: 'var(--text-primary)' }}>
                {data.active_customers}
              </div>
            </div>
          </div>

          {/* Revenue Trend */}
          {data.monthly_trend.length > 0 && (
            <div className="p-5 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
              <h2 className="text-lg font-display mb-4" style={{ color: 'var(--text-primary)' }}>
                Monthly Revenue Trend
              </h2>
              <div className="flex items-end gap-2 h-40">
                {data.monthly_trend.map((m, i) => {
                  const max = Math.max(...data.monthly_trend.map(t => t.total), 1);
                  const h = (m.total / max) * 100;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        {formatCurrency(m.total)}
                      </span>
                      <div
                        className="w-full rounded-t"
                        style={{ height: `${h}%`, background: 'var(--netrun-green)', minHeight: 4 }}
                      />
                      <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                        {m.month ? new Date(m.month).toLocaleDateString('en-US', { month: 'short' }) : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Revenue by Product */}
            <div className="p-5 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
              <h2 className="text-lg font-display mb-4 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                <BarChart3 size={18} /> Revenue by Product
              </h2>
              {data.by_product.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No product revenue data yet</p>
              ) : (
                <div className="space-y-3">
                  {data.by_product.map((p, i) => {
                    const max = data.by_product[0]?.total || 1;
                    return (
                      <div key={i}>
                        <div className="flex justify-between text-sm mb-1">
                          <span style={{ color: 'var(--text-secondary)' }}>{p.product}</span>
                          <span style={{ color: 'var(--netrun-green)' }}>{formatCurrency(p.total)}</span>
                        </div>
                        <div className="h-2 rounded-full" style={{ background: 'var(--bg-primary)' }}>
                          <div
                            className="h-2 rounded-full"
                            style={{ width: `${(p.total / max) * 100}%`, background: 'var(--netrun-green)' }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Revenue by Organization */}
            <div className="p-5 rounded-xl" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)' }}>
              <h2 className="text-lg font-display mb-4" style={{ color: 'var(--text-primary)' }}>
                Top Organizations by Revenue
              </h2>
              {data.by_organization.length === 0 ? (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No organization revenue data yet</p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ color: 'var(--text-muted)' }}>
                      <th className="text-left pb-2">Organization</th>
                      <th className="text-right pb-2">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_organization.map((o, i) => (
                      <tr key={i} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                        <td className="py-2" style={{ color: 'var(--text-secondary)' }}>{o.organization}</td>
                        <td className="py-2 text-right font-mono" style={{ color: 'var(--netrun-green)' }}>
                          {formatCurrency(o.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
